// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import assertNever from "assert-never";
import DOMPurify from "dompurify";
import { makeAutoObservable } from "mobx";
import { renderToString } from "react-dom/server";

import { OfficeAddress } from "~datatypes";

import AnalyticsStore from "../../RootStore/AnalyticsStore";
import {
  RoutePlannerRouteEvent,
  RoutePlannerRouteMetadata,
} from "../../RootStore/AnalyticsStore/AnalyticsStore";
import { TenantId } from "../../RootStore/types";
import { formatStateAddress, formatWorkflowsDate } from "../../utils";
import { WorkflowsStore } from "../../WorkflowsStore";
import { Officer } from "../../WorkflowsStore/Officer";
import RoutePlannerClientStore from "./ClientStore/ClientStoreBase";
import { RoutePlannerClientsPresenter } from "./RoutePlannerClientsPresenter";
import { SelectedClientsEmailTemplate } from "./RoutePlannerEmailTemplate";

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
// The Google Maps Embed API is documented here: https://developers.google.com/maps/documentation/embed/embedding-map
const BASE_EMBED_URL_NO_POINTS = "https://www.google.com/maps/embed/v1/view";
const BASE_EMBED_URL = "https://www.google.com/maps/embed/v1/directions";

// The Google Maps directions URLs API is documented here: https://developers.google.com/maps/documentation/urls/get-started
const BASE_DIRECTIONS_URL = "https://www.google.com/maps/dir/";

type MapDirectionsRequestProps = {
  userEmail: string;
  emailSubject: string;
  emailBody: string;
};

const VALID_STATES = ["US_TX", "US_ID"] as const;

type RoutePlannerStateCode = (typeof VALID_STATES)[number];

function isValidState(value: TenantId): value is RoutePlannerStateCode {
  return VALID_STATES.includes(value as RoutePlannerStateCode);
}

function formatOfficeAddress(
  address: OfficeAddress,
  stateCode: RoutePlannerStateCode,
): string {
  const state = stateCode.split("_")[1].toUpperCase();

  return formatStateAddress(
    `${address.line1}${address.line2 ? " " + address.line2 : ""}, ${address.city}, ${state} ${address.zip}`,
  );
}

/**
 * Responsible for handling map-related data for the Tasks Route Planner page.
 * The child clientsPresenter keeps track of the list of selected officers
 * and clients.
 */
export class RoutePlannerPresenter {
  public readonly clientsPresenter: RoutePlannerClientsPresenter;
  private readonly analyticsStore: AnalyticsStore;
  private _userPickedStartingAddress: string | undefined = undefined;
  private _userPickedEndingAddress: string | undefined = undefined;
  private _isEndingAddressMatchingStart = false;
  private stateCode: RoutePlannerStateCode;

  constructor(
    private readonly workflowsStore: WorkflowsStore,
    private readonly routePlannerClientStore: RoutePlannerClientStore,
  ) {
    this.routePlannerClientStore = routePlannerClientStore;
    this.clientsPresenter = new RoutePlannerClientsPresenter(
      workflowsStore,
      this.routePlannerClientStore,
    );
    this.analyticsStore = workflowsStore.rootStore.analyticsStore;

    if (
      workflowsStore.rootStore.currentTenantId &&
      isValidState(workflowsStore.rootStore.currentTenantId)
    ) {
      this.stateCode = workflowsStore.rootStore.currentTenantId;
    } else {
      throw new Error(
        `${workflowsStore.rootStore.currentTenantId} is not a valid state for the Route Planner`,
      );
    }
    makeAutoObservable(this);
  }

  // if the user does not have add more feature variant then we do not show
  get showAddMoreButton() {
    return !!this.workflowsStore.rootStore.userStore.activeFeatureVariants
      .HCRPAddMoreClients;
  }

  updateShowWindow() {
    this.routePlannerClientStore.updateShowWindow();
  }

  get showWindow() {
    return this.routePlannerClientStore.showAddMoreClientWindow;
  }

  get mapsApiKey(): string {
    return API_KEY;
  }

  // Starting address picker and autocomplete settings

  set userPickedStartingAddress(newAddress: string | undefined) {
    this.analyticsStore.trackRoutePlannerStartingAddressChanged();
    this._userPickedStartingAddress = newAddress;

    // Keep ending address in sync when the "match" checkbox is checked
    if (this._isEndingAddressMatchingStart) {
      this._userPickedEndingAddress = newAddress;
    }
  }

  get startingAddress() {
    return this._userPickedStartingAddress ?? this.startingAddressPlaceholder;
  }

  set userPickedEndingAddress(newAddress: string | undefined) {
    this.analyticsStore.trackRoutePlannerEndingAddressChanged({
      hadPreviousAddress: !!this.endingAddress,
    });
    this._userPickedEndingAddress = newAddress;

    // Uncheck the matching checkbox if the ending address no longer matches the starting address
    if (
      this._isEndingAddressMatchingStart &&
      newAddress !== this.startingAddress
    ) {
      this._isEndingAddressMatchingStart = false;
    }
  }

  get userPickedEndingAddress(): string | undefined {
    return this._userPickedEndingAddress;
  }

  get endingAddress(): string | undefined {
    return this._userPickedEndingAddress;
  }

  get hasEndingAddress(): boolean {
    return this._userPickedEndingAddress !== undefined;
  }

  get endingAddressPlaceholder(): string {
    return this.startingAddressPlaceholder;
  }

  copyStartToEnd() {
    if (this.startingAddress) {
      this.analyticsStore.trackRoutePlannerEndingAddressCopiedFromStart();
      this._userPickedEndingAddress = this.startingAddress;
    }
  }

  clearEndingAddress() {
    this._userPickedEndingAddress = undefined;
  }

  get isEndingAddressMatchingStart(): boolean {
    return this._isEndingAddressMatchingStart;
  }

  setEndingAddressMatchingStart(value: boolean) {
    this._isEndingAddressMatchingStart = value;
    if (value) {
      this.copyStartToEnd();
    } else {
      this.clearEndingAddress();
    }
  }

  get startingAddressPlaceholder(): string {
    const { selectedClients } = this.clientsPresenter;
    let addresses;
    if (selectedClients.length > 0) {
      // If we have any selected people, get potential starting addresses from
      // the officers of the selected people.
      addresses = selectedClients
        .map((client) => client.assignedStaff)
        .map((officer) => {
          if (officer?.recordType !== "supervisionStaff") {
            return;
          }
          switch (officer.stateSpecificData?.stateCode) {
            case "US_TX":
              return officer.stateSpecificData.dpoAddress;
            case "US_ID":
              return officer.stateSpecificData.officeAddress;
            default:
              return;
          }
        });
    } else {
      // Otherwise, get addresses for all searched-for officers
      addresses = this.workflowsStore.searchStore.selectedSearchables.map(
        (searchable) => {
          if (
            !(searchable instanceof Officer) ||
            searchable.record.recordType !== "supervisionStaff"
          ) {
            return;
          }
          switch (searchable.record.stateSpecificData?.stateCode) {
            case "US_TX":
              return searchable.record.stateSpecificData.dpoAddress;
            case "US_ID":
              return searchable.record.stateSpecificData.officeAddress;
            default:
              return;
          }
        },
      );
    }

    // Pick the first valid address from the list
    const formattedAddresses = addresses
      .filter((address) => !!address)
      .map((address) => formatOfficeAddress(address, this.stateCode));

    if (formattedAddresses.length !== 0) {
      return formattedAddresses[0];
    }
    return "";
  }
  /**
   * Return the center in meters of the circular region to limit address predictions toward
   */
  get locationBias(): { lat: number; lng: number } {
    switch (this.stateCode) {
      case "US_TX":
        return { lat: 31.2, lng: -99.9 };
      case "US_ID":
        return { lat: 45.3, lng: -114.2 };
      default:
        return assertNever(this.stateCode);
    }
  }

  /**
   * Return the radius in meters of the circular region to limit address predictions toward
   */
  get radius(): number {
    switch (this.stateCode) {
      case "US_TX":
        return 660000;
      case "US_ID":
        return 455302;
      default:
        return assertNever(this.stateCode);
    }
  }

  /**
   * Return the zoom level to use for the map. This can range from 0 to 21.
   * https://developers.google.com/maps/documentation/embed/embedding-map#view_mode
   */
  get zoomLevel(): number {
    return 6;
  }

  // Emailing the user with the URL of the map route

  /**
   * Return a URL that can be opened in a browser to show the current route in Google Maps.
   * The URL is structured using the Google Maps URLs API in "directions" mode.
   */
  get mapDirectionsUrl(): string {
    const { selectedFormattedAddresses, selectedPlaceIds } =
      this.clientsPresenter;

    // If an ending address is specified, all client addresses are waypoints
    // Otherwise, the last client address is the destination
    const hasEndingAddress = this.hasEndingAddress;
    const waypointAddresses = hasEndingAddress
      ? selectedFormattedAddresses
      : selectedFormattedAddresses.slice(0, -1);
    const waypointPlaceIds = hasEndingAddress
      ? selectedPlaceIds
      : selectedPlaceIds.slice(0, -1);

    const formattedWaypoints = waypointAddresses.join("|");
    const formattedWaypointPlaceIds = waypointPlaceIds.join("|");

    const destination = hasEndingAddress
      ? this.endingAddress
      : selectedFormattedAddresses[selectedFormattedAddresses.length - 1];
    const destinationPlaceId = hasEndingAddress
      ? undefined
      : selectedPlaceIds[selectedPlaceIds.length - 1];

    const queryParams: Record<string, string | number> = {
      api: 1,
      travelmode: "driving",
      origin: this.startingAddress,
      destination: destination ?? "",
      // Only include destination_place_id if we're using a client address as destination
      ...(destinationPlaceId
        ? { destination_place_id: destinationPlaceId }
        : {}),
      // Only include waypoints if there is at least one; an empty waypoints parameter causes an error
      ...(waypointAddresses.length >= 1
        ? {
            waypoints: formattedWaypoints,
            waypoint_place_ids: formattedWaypointPlaceIds,
          }
        : {}),
    };

    const formattedQueryParams = Object.entries(queryParams)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");

    return `${BASE_DIRECTIONS_URL}?${formattedQueryParams}`;
  }

  /**
   * Return the title each state names their ID data columns
   */
  get idTitle(): string {
    switch (this.stateCode) {
      case "US_TX":
        return "SID";
      case "US_ID":
        return "IDOC";
      default:
        return assertNever(this.stateCode);
    }
  }

  /**
   * Return an HTML-formatted email containing the directions the user has selected,
   * or undefined if no valid email request body can be created.
   */
  get mapDirectionsBody(): MapDirectionsRequestProps | undefined {
    if (!this.userEmailAddress) return;
    const { selectedClients, mapsAddressLink } = this.clientsPresenter;

    const today = formatWorkflowsDate(new Date());

    const piiEmailAccessEnabled =
      !!this.workflowsStore.rootStore.userStore.activeFeatureVariants
        .HCRPPIIEmail;

    const renderedBody = renderToString(
      SelectedClientsEmailTemplate({
        today,
        mapDirectionsUrl: this.mapDirectionsUrl,
        idTitle: this.idTitle,
        selectedClients,
        piiEmailAccessEnabled,
        mapsAddressLink,
      }),
    );

    return {
      userEmail: this.userEmailAddress,
      emailSubject: `Recidiviz Route Planner - Maps Link (${today})`,
      emailBody: DOMPurify.sanitize(renderedBody),
    };
  }

  /**
   * Return the currently-logged-in user's email address
   */
  get userEmailAddress(): string | undefined {
    const { userStore } = this.workflowsStore.rootStore;
    if (userStore.isImpersonating) return;
    return this.workflowsStore.rootStore.userStore.userEmail;
  }

  async sendDirectionsEmail() {
    const requestBody = this.mapDirectionsBody;
    if (!requestBody) {
      return;
    }
    return this.workflowsStore.rootStore.apiStore.client.post(
      `${import.meta.env.VITE_NEW_BACKEND_API_URL}/workflows/external_request/${this.stateCode}/email_user`,
      requestBody,
    );
  }

  // Embedding the map itself

  /**
   * Return a URL that can be used as the source of an iframe to show a route
   * in Google Maps. The URL is structured using the Google Maps Embed API in "directions" mode
   */
  get mapIframeUrl(): string {
    const { selectedPlaceIds } = this.clientsPresenter;

    if (selectedPlaceIds.length === 0) {
      const queryParams = new URLSearchParams({
        key: this.mapsApiKey,
        center: `${this.locationBias.lat},${this.locationBias.lng}`,
        zoom: String(this.zoomLevel),
      }).toString();
      return `${BASE_EMBED_URL_NO_POINTS}?${queryParams}`;
    }

    // If an ending address is specified, all client addresses are waypoints
    // Otherwise, the last client address is the destination
    const hasEndingAddress = this.hasEndingAddress;
    const formattedClientAddresses = selectedPlaceIds.map(
      (placeId) => `place_id:${placeId}`,
    );
    const waypointAddresses = hasEndingAddress
      ? formattedClientAddresses
      : formattedClientAddresses.slice(0, -1);
    const formattedWaypoints = waypointAddresses.join("|");

    const destination = hasEndingAddress
      ? this.endingAddress
      : formattedClientAddresses[formattedClientAddresses.length - 1];

    const queryParams = new URLSearchParams({
      key: this.mapsApiKey,
      mode: "driving",
      origin: this.startingAddress,
      destination: destination ?? "",
      // Only include waypoints if there is at least one; an empty waypoints parameter causes an error
      ...(waypointAddresses.length >= 1
        ? {
            waypoints: formattedWaypoints,
          }
        : {}),
    }).toString();

    return `${BASE_EMBED_URL}?${queryParams}`;
  }

  // Controls related to the mobile map view
  isMapView = false;

  /**
   * Returns whether a button to switch to the map view of the tool should be displayed
   */
  get showMobileMapViewButton() {
    return (
      this.clientsPresenter.selectedClients.length > 0 ||
      this.clientsPresenter.isAddingPerson
    );
  }

  /**
   * Returns copy for a button to switch to the map view of the tool
   */
  get mapViewButtonCopy() {
    return this.clientsPresenter.isAddingPerson
      ? `Loading map...`
      : `See map view (${this.clientsPresenter.selectedClients.length} selected)`;
  }

  // Tracking

  get routeEventMetadata(): RoutePlannerRouteMetadata {
    return {
      hasStartingAddress: !!this.startingAddress,
      hasEndingAddress: !!this.endingAddress,
      waypointCount: this.clientsPresenter.selectedClientPseudoIds.length,
    };
  }

  trackRoutePlannerRouteEvent(eventType: RoutePlannerRouteEvent) {
    this.analyticsStore.trackRoutePlannerRouteEvent(
      eventType,
      this.routeEventMetadata,
    );
  }
}
